/**
 * RptNmethPage - 新方法論報告頁面
 * 19 個 Section 完成度 + 列印/產檔
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProject } from '../contexts/ProjectContext';
import { getNmethRptCompleteness, getNmethRptHtmlReport, downloadNmethRptPdfReport, NmethRptCompletenessData } from '../services/nmethRptService';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { usePermission } from '../hooks/usePermission';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const RptNmethPage: React.FC = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { selectedProposalId, selectedProposal } = useProject();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const canPrint = hasPermission('rpt_nmeth', 'print');
  const canFile = hasPermission('rpt_nmeth', 'file');
  const [data, setData] = useState<NmethRptCompletenessData | null>(null);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCompleteness = useCallback(async (proposalId: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getNmethRptCompleteness(proposalId);
      setData(result);
    } catch (e: any) {
      setError(e?.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isZh]);

  useEffect(() => {
    if (selectedProposalId) {
      loadCompleteness(selectedProposalId);
    } else {
      setData(null);
    }
  }, [selectedProposalId, loadCompleteness]);

  const handlePrint = async () => {
    if (!selectedProposalId) return;
    setPrinting(true);
    try {
      const html = await getNmethRptHtmlReport(selectedProposalId);
      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); }
    } catch (e: any) {
      toast.error((isZh ? '列印失敗：' : 'Print failed: ') + (e?.response?.data?.detail || e.message));
    } finally {
      setPrinting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedProposalId) return;
    setExporting(true);
    try {
      const title = data?.proposal_title || `Methodology_${selectedProposalId}`;
      const cleanTitle = title.replace(/<[^>]+>/g, '').replace(/[^a-zA-Z0-9\u4e00-\u9fff]/g, '_');
      await downloadNmethRptPdfReport(selectedProposalId, `METH_${cleanTitle}.pdf`);
    } catch (e: any) {
      toast.error((isZh ? '產檔失敗：' : 'Export failed: ') + (e?.response?.data?.detail || e.message));
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (pct: number, hasData: boolean) => {
    if (!hasData) return '#9e9e9e';
    if (pct >= 80) return '#43a047';
    if (pct >= 40) return '#fb8c00';
    if (pct > 0) return '#e53935';
    return '#9e9e9e';
  };

  const getStatusLabel = (pct: number, hasData: boolean) => {
    if (!hasData) return isZh ? '未填寫' : 'Empty';
    if (pct >= 80) return isZh ? '完成' : 'Done';
    if (pct >= 40) return isZh ? '進行中' : 'In Progress';
    if (pct > 0) return isZh ? '未完成' : 'Incomplete';
    return isZh ? '未填寫' : 'Empty';
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader
          funcCode="rpt_nmeth"
          subtitle={selectedProposal?.title ? selectedProposal.title.replace(/<[^>]+>/g, '') : undefined}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canPrint && (
            <button className="btn-primary" onClick={handlePrint} disabled={printing || loading || !data}>
              {printing ? (isZh ? '列印中...' : 'Printing...') : `🖨️ ${isZh ? '列印' : 'Print'}`}
            </button>
          )}
          {canFile && (
            <button className="btn-primary" onClick={handleExportPdf} disabled={exporting || loading || !data}
              style={{ background: exporting ? '#90a4ae' : '#388e3c' }}>
              {exporting ? (isZh ? '產檔中...' : 'Exporting...') : `📄 ${isZh ? '產檔 (PDF)' : 'Export (PDF)'}`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {isZh ? '請先在上方選擇方法論申請文件' : 'Please select a methodology proposal from the top bar'}
        </div>
      )}

      {loading && <div className="loading">{isZh ? '載入中...' : 'Loading...'}</div>}

      {data && !loading && (
        <div>
          {/* 整體完成度 */}
          <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 6, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>{isZh ? '整體完成度' : 'Overall Progress'}</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: getStatusColor(data.overall_percentage, true) }}>
                {data.overall_percentage}%
              </span>
            </div>
            <div style={{ height: 10, background: '#e0e0e0', borderRadius: 5, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${data.overall_percentage}%`, background: getStatusColor(data.overall_percentage, true), borderRadius: 5, transition: 'width 0.5s ease' }} />
            </div>
          </div>

          {/* 19 Section 清單 */}
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 100 }}>{isZh ? 'Section' : 'Section'}</th>
                <th>{isZh ? '名稱' : 'Name'}</th>
                <th style={{ width: 100, textAlign: 'center' }}>{isZh ? '狀態' : 'Status'}</th>
                <th style={{ width: 200, textAlign: 'right' }}>{isZh ? '完成度' : 'Progress'}</th>
              </tr>
            </thead>
            <tbody>
              {data.sections.map((sec) => {
                const color = getStatusColor(sec.percentage, sec.has_data);
                return (
                  <tr key={sec.key}>
                    <td style={{ fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>
                      {sec.key.toUpperCase().replace('_', '.')}
                    </td>
                    <td>{isZh ? sec.label_zh : sec.label_en}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px',
                        background: color + '20', color, borderRadius: 12,
                        fontSize: 12, fontWeight: 600, border: `1px solid ${color}40`,
                      }}>
                        {getStatusLabel(sec.percentage, sec.has_data)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
                        <div style={{ flex: 1, height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', maxWidth: 120 }}>
                          <div style={{ height: '100%', width: `${sec.percentage}%`, background: color, borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color, minWidth: 36, textAlign: 'right' }}>
                          {sec.percentage}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RptNmethPage;
